import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { LanguageSelector } from "@/components/LanguageSelector";
import { Brand } from "@/components/Brand";
import { Wrench, ClipboardList, LogOut, ChevronRight, UserRound } from "lucide-react";

export default function Home() {
  const nav = useNavigate();
  const { customer, logout, refresh } = useAuth();
  const { t } = useLanguage();

  useEffect(() => { refresh(); }, [refresh]);

  const unread = customer?.unread_messages || 0;

  return (
    <div className="min-h-screen bg-background">
      <LanguageSelector />
      <div className="max-w-md mx-auto px-6 py-8">
        <div className="pr-16">
          <Brand subtitle={t("home_title")} />
        </div>
        <p className="text-center text-muted-foreground mt-4">{customer?.name}</p>

        <div className="mt-8 space-y-4 animate-slide-up">
          <button data-testid="home-request-quote-btn" onClick={() => nav("/urgency")}
            className="flex w-full items-center gap-4 rounded-3xl bg-primary text-primary-foreground p-6 shadow-lg hover:-translate-y-1 active:scale-95 transition-transform duration-200">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15">
              <Wrench className="h-6 w-6" />
            </div>
            <div className="text-left flex-1">
              <p className="font-display text-lg font-bold">{t("cta_request_quote")}</p>
            </div>
            <ChevronRight className="h-6 w-6 opacity-80" />
          </button>

          <button data-testid="home-my-requests-btn" onClick={() => nav("/my-requests")}
            className="flex w-full items-center gap-4 rounded-3xl bg-white border border-border p-6 shadow-sm hover:-translate-y-1 active:scale-95 transition-transform duration-200">
            <div className="relative flex h-12 w-12 items-center justify-center rounded-2xl bg-secondary">
              <ClipboardList className="h-6 w-6 text-primary" />
              {unread > 0 && (
                <span data-testid="home-unread-indicator" className="absolute -top-1 -right-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1 text-[11px] font-bold text-destructive-foreground">
                  {unread}
                </span>
              )}
            </div>
            <div className="text-left flex-1">
              <p className="font-display text-lg font-bold text-primary">{t("my_requests")}</p>
              {unread > 0 && <p className="text-xs text-destructive font-medium">{unread} {t("new_messages")}</p>}
            </div>
            <ChevronRight className="h-6 w-6 text-primary/60" />
          </button>

          <button data-testid="home-profile-btn" onClick={() => nav("/profile")}
            className="flex w-full items-center gap-4 rounded-3xl bg-white border border-border p-6 shadow-sm hover:-translate-y-1 active:scale-95 transition-transform duration-200">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-secondary">
              <UserRound className="h-6 w-6 text-primary" />
            </div>
            <div className="text-left flex-1">
              <p className="font-display text-lg font-bold text-primary">{t("my_profile")}</p>
            </div>
            <ChevronRight className="h-6 w-6 text-primary/60" />
          </button>
        </div>

        <p className="mt-8 text-center text-xs text-muted-foreground leading-relaxed">{t("service_area")}</p>

        <button data-testid="home-logout-btn" onClick={() => { logout(); nav("/"); }}
          className="mt-6 flex w-full items-center justify-center gap-2 text-sm text-muted-foreground hover:text-destructive transition-colors">
          <LogOut className="h-4 w-4" /> {t("logout")}
        </button>
      </div>
    </div>
  );
}
